import { useCallback } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  Button,
  IconArrowLeft,
  IconArrowRight,
  InformationalScreen,
  LoadingModal,
  useDirection,
} from '@adc/parallax-component-library';
import { AxiosError } from 'axios';
import { FormContainer, Input, MainContainer } from 'Components/utility';
import { ApiStatus, ErrorCode } from 'Constants';
import { type FormikHelpers, useFormik } from 'formik';
import { useApiError, useAuth } from 'Hooks';
import { updateEmail } from 'Services/patient';
import { type RootState } from 'src/reducers';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

const mapStateToProps = ({
  auth: { account_id },
  env: { osType, osVersion, app, appVersion },
}: RootState) => ({
  accountId: account_id,
  osType,
  osVersion,
  app,
  appVersion,
});

const initialValuesFormik = {
  email: '',
  repeatEmail: '',
};

type UpdateEmailFormValues = typeof initialValuesFormik;

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const UpdateEmail = ({ accountId, osType, osVersion, app, appVersion }: Props) => {
  const validationSchema = z
    .object({
      email: z
        .string(i18n.t('UpdateEmail.content.updateEmailForm.formField.email.errors.required'))
        .trim()
        .min(1, i18n.t('UpdateEmail.content.updateEmailForm.formField.email.errors.required'))
        .email(
          i18n.t(
            'UpdateEmail.content.updateEmailForm.formField.email.errors.emailAddressValidEmail'
          )
        ),
      repeatEmail: z
        .string(i18n.t('UpdateEmail.content.updateEmailForm.formField.repeatEmail.errors.required'))
        .trim()
        .min(1, i18n.t('UpdateEmail.content.updateEmailForm.formField.repeatEmail.errors.required'))
        .email(
          i18n.t(
            'UpdateEmail.content.updateEmailForm.formField.repeatEmail.errors.emailAddressValidEmail'
          )
        ),
    })
    .refine(
      (data) => {
        return data.email === data.repeatEmail;
      },
      {
        path: ['repeatEmail'],
        error: i18n.t(
          'UpdateEmail.content.updateEmailForm.formField.repeatEmail.errors.emailMustMatch'
        ),
      }
    );

  const handleClickBack = useCallback(() => {
    mediator.publish('router:navigate', '/account-information');
  }, []);

  const isLtr = useDirection() === 'ltr';

  const { showApiErrorModal, showNetworkErrorModal, showCustomError } = useApiError();
  //  Reusing error modals
  const { handleLoginError, showHcpError } = useAuth('changepassword');

  const handleApiRequestError = useCallback(
    (
      error: AxiosError<ApiErrorData>,
      resetForm: () => void,
      setFieldValue: (name: string, value: unknown) => void,
      setTouched: (fields: Record<string, boolean>) => void
    ) => {
      switch (error.status) {
        case ApiStatus.FORBIDDEN: {
          const code = error.response?.data.code;

          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showHcpError();
          }

          break;
        }

        case ApiStatus.UNAUTHORIZED:
          handleLoginError(error, resetForm, () => {
            setFieldValue('email', '');
            setFieldValue('repeatEmail', '');
          });
          break;

        case ApiStatus.CONFLICT: {
          setFieldValue('email', '');
          setFieldValue('repeatEmail', '');
          showApiErrorModal();
          setTouched({});
          const errorCode = error.response?.data.code;

          if (errorCode === ApiStatus.PATIENT_EXISTS) {
            showCustomError(i18n.t('Global.modals.emailExists.body'));
          } else {
            showCustomError(error.response?.data?.details || '');
          }

          break;
        }

        case ApiStatus.TOO_MANY_REQUESTS: {
          resetForm();
          break;
        }

        default: {
          if (error.code === ErrorCode.NETWORK_ERROR) {
            showNetworkErrorModal();
          } else {
            showApiErrorModal();
          }
        }
      }
    },
    [handleLoginError, showApiErrorModal, showHcpError, showNetworkErrorModal, showCustomError]
  );

  const onSubmitForm = useCallback(
    async (
      values: UpdateEmailFormValues,
      { setSubmitting, resetForm, setFieldValue, setTouched }: FormikHelpers<UpdateEmailFormValues>
    ) => {
      try {
        await updateEmail(
          {
            userID: accountId,
            email: values.email,
          },
          osType,
          osVersion,
          app,
          appVersion
        );

        notifyApp('adc-webview:update-email');
        mediator.publish('router:navigate', '/account-information');
      } catch (error) {
        handleApiRequestError(
          error as AxiosError<ApiErrorData>,
          resetForm,
          setFieldValue,
          setTouched
        );
      }

      setSubmitting(false);
    },
    [accountId, app, appVersion, osType, osVersion, handleApiRequestError]
  );

  const {
    handleSubmit,
    handleBlur,
    handleChange,
    isValid,
    isSubmitting,
    errors,
    touched,
    values,
    dirty,
  } = useFormik({
    initialValues: initialValuesFormik,
    onSubmit: onSubmitForm,
    validateOnBlur: true,
    validationSchema: toFormikValidationSchema(validationSchema),
  });

  return (
    <MainContainer>
      {isSubmitting && <LoadingModal aria-label="Loading" />}

      <InformationalScreen>
        <InformationalScreen.Page
          topNavigationProps={{
            title: i18n.t('UpdateEmail.title'),
            buttonStart: {
              element: isLtr ? (
                <IconArrowLeft testID="UpdateEmail.back-button" />
              ) : (
                <IconArrowRight testID="UpdateEmail.back-button" />
              ),
              label: i18n.t('UpdateEmail.title'),
              onPress: handleClickBack,
            },
            testID: 'UpdateEmail.title',
          }}
        >
          <InformationalScreen.Content>
            <FormContainer hasHiddenUserInput>
              <Input
                aria-label={i18n.t('UpdateEmail.content.updateEmailForm.formField.email.label')}
                placeholder={i18n.t(
                  'UpdateEmail.content.updateEmailForm.formField.email.placeholder'
                )}
                label={i18n.t('UpdateEmail.content.updateEmailForm.formField.email.label')}
                errorMessage={errors.email}
                isInvalid={Boolean(errors.email && touched.email)}
                testID="UpdateEmail.email"
                value={values.email}
                onChangeText={handleChange('email')}
                onBlur={handleBlur('email')}
                inputMode="email"
              />
              <Input
                aria-label={i18n.t(
                  'UpdateEmail.content.updateEmailForm.formField.repeatEmail.label'
                )}
                placeholder={i18n.t(
                  'UpdateEmail.content.updateEmailForm.formField.repeatEmail.placeholder'
                )}
                label={i18n.t('UpdateEmail.content.updateEmailForm.formField.repeatEmail.label')}
                errorMessage={errors.repeatEmail}
                isInvalid={Boolean(errors.repeatEmail && touched.repeatEmail)}
                testID="UpdateEmail.repeatEmail"
                value={values.repeatEmail}
                onChangeText={handleChange('repeatEmail')}
                onBlur={handleBlur('repeatEmail')}
                inputMode="email"
              />
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Button
              label={i18n.t('Global.microcopy.common.save')}
              disabled={!isValid || isSubmitting || !dirty}
              onPress={() => {
                handleSubmit();
              }}
              testID="UpdateEmail.save-button"
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(UpdateEmail);
